import { BudgetExpenseOrIncome, BudgetSections } from 'business/constants';
import {
    IndicatorFilterRow,
    getFieldWithReportFromField,
} from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/BudgetBalance/helpers';
import {
    SIMULATED_ARBITRATED_AMOUNT,
    SERVICE_PROPOSAL_AMOUNT,
    PRIMITIVE_BUDGET_AMOUNT,
    VOTED_TOTAL_FIELD,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    VOTED_FIELD,
    REALIZED_FIELD,
    CHAPTER_FIELD,
    NATURE_FIELD,
    BUDGET_BALANCE_TOTAL_FIELD,
    REPORTED_FIELD,
    ARBITRAGE_DM_AMOUNT,
} from 'routes/BudgetModule/config/constants';
import { isAmp } from 'routes/BudgetModule/helpers/bind';
import { getClientBind } from 'store/APIClient';

export const BUDGET_LINES_BALANCE_METRICS = [
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
    REALIZED_FIELD,
];

export const INSCRIPTIONS_BALANCE_METRICS = isAmp(getClientBind())
    ? [
          SERVICE_PROPOSAL_AMOUNT,
          SIMULATED_ARBITRATED_AMOUNT,
          PRIMITIVE_BUDGET_AMOUNT,
          SUPPLEMENTARY_BUDGET_AMOUNT,
          ARBITRAGE_DM_AMOUNT,
          BUDGET_BALANCE_TOTAL_FIELD,
      ]
    : [
          SERVICE_PROPOSAL_AMOUNT,
          SIMULATED_ARBITRATED_AMOUNT,
          PRIMITIVE_BUDGET_AMOUNT,
          SUPPLEMENTARY_BUDGET_AMOUNT,
          BUDGET_BALANCE_TOTAL_FIELD,
      ];

const INSCRIPTIONS_WITH_REPORTED_AMOUNT = [
    REPORTED_FIELD,
    ...INSCRIPTIONS_BALANCE_METRICS.map(getFieldWithReportFromField),
];

export const BALANCE_METRICS = [
    ...BUDGET_LINES_BALANCE_METRICS,
    ...INSCRIPTIONS_BALANCE_METRICS,
    ...INSCRIPTIONS_WITH_REPORTED_AMOUNT,
];

/**
 * INDICATORS:
 * Here we define the name and the traduction key for totals indicators
 * that may be used in budget synthesis tables
 */

export const EVERYDAY_MANAGEMENT_OPERATING_EXPENSES_TOTAL =
    'budgetBalance.everydayManagementOperatingExpensesTotal';
export const REAL_OPERATING_EXPENSES_TOTAL =
    'budgetBalance.realOperatingExpensesTotal';
export const ORDER_OPERATING_EXPENSES_TOTAL =
    'budgetBalance.orderOperatingExpensesTotal';
export const OTHER_OPERATING_EXPENSES_TOTAL =
    'budgetBalance.otherOperatingExpensesTotal';
export const OPERATING_EXPENSES_SUB_TOTAL =
    'budgetBalance.operatingExpensesSubTotal';
export const OPERATING_EXPENSES_TOTAL = 'budgetBalance.operatingExpensesTotal';

export const EVERYDAY_MANAGEMENT_OPERATING_INCOMES_TOTAL =
    'budgetBalance.everydayManagementOperatingIncomesTotal';
export const REAL_OPERATING_INCOMES_TOTAL =
    'budgetBalance.realOperatingIncomesTotal';
export const ORDER_OPERATING_INCOMES_TOTAL =
    'budgetBalance.orderOperatingIncomesTotal';
export const OTHER_OPERATING_INCOMES_TOTAL =
    'budgetBalance.otherOperatingIncomesTotal';
export const OPERATING_INCOMES_SUB_TOTAL =
    'budgetBalance.operatingIncomesSubTotal';
export const OPERATING_INCOMES_TOTAL = 'budgetBalance.operatingIncomesTotal';

export const EQUIPMENT_INVESTMENT_EXPENSES_TOTAL =
    'budgetBalance.equipmentInvestmentExpensesTotal';
export const FINANCE_INVESTMENT_EXPENSES_TOTAL =
    'budgetBalance.financeInvestmentExpensesTotal';
export const REAL_INVESTMENT_EXPENSES_TOTAL =
    'budgetBalance.realInvestmentExpensesTotal';
export const ORDER_INVESTMENT_EXPENSES_TOTAL =
    'budgetBalance.orderInvestmentExpensesTotal';
export const OTHER_INVESTMENT_EXPENSES_TOTAL =
    'budgetBalance.otherInvestmentExpensesTotal';
export const INVESTMENT_EXPENSES_SUB_TOTAL =
    'budgetBalance.investmentExpensesSubTotal';
export const INVESTMENT_EXPENSES_TOTAL =
    'budgetBalance.investmentExpensesTotal';

export const EQUIPMENT_INVESTMENT_INCOMES_TOTAL =
    'budgetBalance.equipmentInvestmentIncomesTotal';
export const FINANCE_INVESTMENT_INCOMES_TOTAL =
    'budgetBalance.financeInvestmentIncomesTotal';
export const REAL_INVESTMENT_INCOMES_TOTAL =
    'budgetBalance.realInvestmentIncomesTotal';
export const ORDER_INVESTMENT_INCOMES_TOTAL =
    'budgetBalance.orderInvestmentIncomesTotal';
export const OTHER_INVESTMENT_INCOMES_TOTAL =
    'budgetBalance.otherInvestmentIncomesTotal';
export const INVESTMENT_INCOMES_SUB_TOTAL =
    'budgetBalance.investmentIncomesSubTotal';
export const INVESTMENT_INCOMES_TOTAL = 'budgetBalance.investmentIncomesTotal';

/**
 * INDICATOR FILTERS:
 * Indicators are the sum of amounts for lines respecting some well-defined
 * rules, being specified chapters and natures.
 */

export const INDICATORS_FILTERS: Record<
    string,
    (row: IndicatorFilterRow, isSorting?: boolean) => boolean
> = {
    // First table
    [EVERYDAY_MANAGEMENT_OPERATING_EXPENSES_TOTAL]: (row): boolean =>
        ['011', '012', '014', '016', '017', '65', '6586'].includes(
            row[CHAPTER_FIELD],
        ),
    [REAL_OPERATING_EXPENSES_TOTAL]: (row, isSorting): boolean => {
        const isTargetChapter = ['66', '67', '68', '022'].includes(
            row[CHAPTER_FIELD],
        );
        if (isSorting) return isTargetChapter;
        return (
            isTargetChapter ||
            INDICATORS_FILTERS[EVERYDAY_MANAGEMENT_OPERATING_EXPENSES_TOTAL](
                row,
            )
        );
    },
    [ORDER_OPERATING_EXPENSES_TOTAL]: (row): boolean =>
        ['023', '042', '043'].includes(row[CHAPTER_FIELD]),
    [OTHER_OPERATING_EXPENSES_TOTAL]: (row): boolean =>
        ![
            '002',
            '011',
            '012',
            '014',
            '016',
            '017',
            '65',
            '6586',
            '66',
            '67',
            '68',
            '022',
            '023',
            '042',
            '043',
        ].includes(row[CHAPTER_FIELD]),
    [OPERATING_EXPENSES_SUB_TOTAL]: (row): boolean =>
        !['002'].includes(row[CHAPTER_FIELD]),
    // Second table
    [EVERYDAY_MANAGEMENT_OPERATING_INCOMES_TOTAL]: (row): boolean =>
        ['013', '016', '017', '70', '73', '731', '74', '75'].includes(
            row[CHAPTER_FIELD],
        ),
    [REAL_OPERATING_INCOMES_TOTAL]: (row, isSorting): boolean => {
        const isTargetChapter = ['76', '77', '78'].includes(row[CHAPTER_FIELD]);
        if (isSorting) return isTargetChapter;
        return (
            isTargetChapter ||
            INDICATORS_FILTERS[EVERYDAY_MANAGEMENT_OPERATING_INCOMES_TOTAL](row)
        );
    },
    [ORDER_OPERATING_INCOMES_TOTAL]: (row): boolean =>
        ['042', '043'].includes(row[CHAPTER_FIELD]),
    [OTHER_OPERATING_INCOMES_TOTAL]: (row): boolean =>
        ![
            '002',
            '013',
            '016',
            '017',
            '70',
            '73',
            '731',
            '74',
            '75',
            '76',
            '77',
            '78',
            '042',
            '043',
        ].includes(row[CHAPTER_FIELD]),
    [OPERATING_INCOMES_SUB_TOTAL]: (row): boolean =>
        !['002'].includes(row[CHAPTER_FIELD]),
    // Third table
    [EQUIPMENT_INVESTMENT_EXPENSES_TOTAL]: (row): boolean =>
        ['010', '018', '20', '204', '21', '22', '23'].includes(
            row[CHAPTER_FIELD],
        ),
    [FINANCE_INVESTMENT_EXPENSES_TOTAL]: (row): boolean =>
        ['10', '13', '16', '18', '26', '27', '020'].includes(
            row[CHAPTER_FIELD],
        ),
    [REAL_INVESTMENT_EXPENSES_TOTAL]: (row, isSorting): boolean => {
        const isTargetChapter = ['45'].includes(row[CHAPTER_FIELD]);
        if (isSorting) return isTargetChapter;
        return (
            ['45'].includes(row[CHAPTER_FIELD]) ||
            INDICATORS_FILTERS[EQUIPMENT_INVESTMENT_EXPENSES_TOTAL](row) ||
            INDICATORS_FILTERS[FINANCE_INVESTMENT_EXPENSES_TOTAL](row)
        );
    },
    [ORDER_INVESTMENT_EXPENSES_TOTAL]: (row): boolean =>
        ['040', '041'].includes(row[CHAPTER_FIELD]),
    [OTHER_INVESTMENT_EXPENSES_TOTAL]: (row): boolean =>
        ![
            '001',
            '010',
            '018',
            '20',
            '204',
            '21',
            '22',
            '23',
            '10',
            '13',
            '16',
            '18',
            '26',
            '27',
            '020',
            '45',
            '040',
            '041',
        ].includes(row[CHAPTER_FIELD]),
    [INVESTMENT_EXPENSES_SUB_TOTAL]: (row): boolean =>
        !['001'].includes(row[CHAPTER_FIELD]),
    // Fourth table
    [EQUIPMENT_INVESTMENT_INCOMES_TOTAL]: (row): boolean =>
        ['010', '018', '13', '16', '20', '204', '21', '22', '23'].includes(
            row[CHAPTER_FIELD],
        ) && !['138', '165', '166', '16449'].includes(row[NATURE_FIELD]),
    [FINANCE_INVESTMENT_INCOMES_TOTAL]: (row): boolean =>
        ['10', '1068', '138', '18', '26', '27', '024'].includes(
            row[CHAPTER_FIELD],
        ) || ['138', '165', '166', '16449'].includes(row[NATURE_FIELD]),
    [REAL_INVESTMENT_INCOMES_TOTAL]: (row, isSorting): boolean => {
        const isTargetChapter = ['45'].includes(row[CHAPTER_FIELD]);
        if (isSorting) return isTargetChapter;
        return (
            isTargetChapter ||
            INDICATORS_FILTERS[EQUIPMENT_INVESTMENT_INCOMES_TOTAL](row) ||
            INDICATORS_FILTERS[FINANCE_INVESTMENT_INCOMES_TOTAL](row)
        );
    },
    [ORDER_INVESTMENT_INCOMES_TOTAL]: (row): boolean =>
        ['021', '040', '041'].includes(row[CHAPTER_FIELD]),
    [OTHER_INVESTMENT_INCOMES_TOTAL]: (row): boolean =>
        ![
            '001',
            '010',
            '018',
            '13',
            '16',
            '20',
            '204',
            '21',
            '22',
            '23',
            '45',
            '021',
            '040',
            '041',
            '10',
            '1068',
            '138',
            '18',
            '26',
            '27',
            '024',
        ].includes(row[CHAPTER_FIELD]),
    [INVESTMENT_INCOMES_SUB_TOTAL]: (row): boolean =>
        !['001'].includes(row[CHAPTER_FIELD]),
};

export const ALL_INDICATORS: string[] = Object.keys(
    INDICATORS_FILTERS,
) as string[];

export const BUDGET_BALANCE_TABLES = [
    {
        title: 'budgetBalance.expenseOperating',
        expenseOrIncome: BudgetExpenseOrIncome.EXPENSE,
        budgetSection: BudgetSections.OPERATING,
        additionalIndicators: [
            EVERYDAY_MANAGEMENT_OPERATING_EXPENSES_TOTAL,
            REAL_OPERATING_EXPENSES_TOTAL,
            ORDER_OPERATING_EXPENSES_TOTAL,
            OTHER_OPERATING_EXPENSES_TOTAL,
            OPERATING_EXPENSES_SUB_TOTAL,
        ],
        finalIndicator: OPERATING_EXPENSES_TOTAL,
    },
    {
        title: 'budgetBalance.incomeOperating',
        expenseOrIncome: BudgetExpenseOrIncome.INCOME,
        budgetSection: BudgetSections.OPERATING,
        additionalIndicators: [
            EVERYDAY_MANAGEMENT_OPERATING_INCOMES_TOTAL,
            REAL_OPERATING_INCOMES_TOTAL,
            ORDER_OPERATING_INCOMES_TOTAL,
            OTHER_OPERATING_INCOMES_TOTAL,
            OPERATING_INCOMES_SUB_TOTAL,
        ],
        finalIndicator: OPERATING_INCOMES_TOTAL,
    },
    {
        title: 'budgetBalance.expenseInvestment',
        expenseOrIncome: BudgetExpenseOrIncome.EXPENSE,
        budgetSection: BudgetSections.INVESTMENT,
        additionalIndicators: [
            EQUIPMENT_INVESTMENT_EXPENSES_TOTAL,
            FINANCE_INVESTMENT_EXPENSES_TOTAL,
            REAL_INVESTMENT_EXPENSES_TOTAL,
            ORDER_INVESTMENT_EXPENSES_TOTAL,
            OTHER_INVESTMENT_EXPENSES_TOTAL,
            INVESTMENT_EXPENSES_SUB_TOTAL,
        ],
        finalIndicator: INVESTMENT_EXPENSES_TOTAL,
    },
    {
        title: 'budgetBalance.incomeInvestment',
        expenseOrIncome: BudgetExpenseOrIncome.INCOME,
        budgetSection: BudgetSections.INVESTMENT,
        additionalIndicators: [
            EQUIPMENT_INVESTMENT_INCOMES_TOTAL,
            FINANCE_INVESTMENT_INCOMES_TOTAL,
            REAL_INVESTMENT_INCOMES_TOTAL,
            ORDER_INVESTMENT_INCOMES_TOTAL,
            OTHER_INVESTMENT_INCOMES_TOTAL,
            INVESTMENT_INCOMES_SUB_TOTAL,
        ],
        finalIndicator: INVESTMENT_INCOMES_TOTAL,
    },
];
